import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, useWindowDimensions } from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { galleryAPI, achievementAPI } from '../services/api';
import { GalleryImage as GalleryImageView } from '../components/gallery/GalleryImage';
import { StudentAchievement } from '../types';
import { Logger } from '../utils/logger';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { WebPageShell } from '../components/common/WebPageShell';
import { useTheme, ThemeColors } from '../context/ThemeContext';

interface GalleryImage {
  id: string;
}

const formatMonthYear = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

export function GalleryScreen({ isNested }: { isNested?: boolean } = {}) {
  const { selectedStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [achievements, setAchievements] = useState<StudentAchievement[]>([]);
  const [loadingAchievements, setLoadingAchievements] = useState(true);

  const loadGallery = useCallback(async () => {
    const classId = selectedStudent?.classId;
    if (!classId) {
      setIsLoading(false);
      setImages([]);
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await galleryAPI.getGalleriesByClass(classId);
      const galleries = data?.galleries ?? [];
      setImages(galleries.map((g: any) => ({ id: g.id })));
    } catch (err) {
      Logger.error('Gallery', 'Failed to load class gallery', err);
      setImages([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedStudent?.classId]);

  // Same read-only endpoint the web frontend's student profile page
  // (src/frontend's /org/school/myclass/student/profile, useAchievements.ts) uses.
  const loadAchievements = useCallback(async () => {
    const studentId = selectedStudent?.id;
    if (!studentId) {
      setLoadingAchievements(false);
      setAchievements([]);
      return;
    }
    setLoadingAchievements(true);
    try {
      const { data } = await achievementAPI.getStudentAchievements(studentId);
      const list: StudentAchievement[] = Array.isArray(data) ? data : [];
      setAchievements(list.filter((a) => a.isActive !== false));
    } catch (err) {
      Logger.error('Gallery', 'Failed to load achievements', err);
      setAchievements([]);
    } finally {
      setLoadingAchievements(false);
    }
  }, [selectedStudent?.id]);

  useEffect(() => {
    loadGallery();
    loadAchievements();
  }, [loadGallery, loadAchievements]);

  const content = (
    <View style={{ flexGrow: 1 }}>
      <Text style={styles.title}>School Gallery</Text>

      {isLoading ? (
        <LoadingSpinner message="Loading the gallery..." />
      ) : images.length === 0 ? (
        <View style={styles.empty}>
          <FeatherIcon name="image" size={48} color={colors.textMuted} />
          <Text style={styles.emptyText}>Gallery feature coming soon!</Text>
        </View>
      ) : (
        <View style={[styles.gridContainer, isMobile && styles.gridContainerMobile] as any}>
          {images.map(item => (
            <TouchableOpacity
              key={item.id}
              style={styles.imageContainer}
              onPress={() => {
                setSelectedImage(item);
                setShowModal(true);
              }}>
              <GalleryImageView galleryId={item.id} style={styles.image} containerStyle={styles.image} />
            </TouchableOpacity>
          ))}
        </View>
      )}

      <Text style={styles.sectionTitle}>Achievements</Text>

      {loadingAchievements ? (
        <LoadingSpinner size="small" />
      ) : achievements.length === 0 ? (
        <View style={styles.empty}>
          <FeatherIcon name="award" size={32} color={colors.textMuted} />
          <Text style={styles.emptyText}>No achievements recorded yet.</Text>
        </View>
      ) : (
        achievements.map((ach) => (
          <View key={ach.id} style={[styles.achieveCard, isMobile && styles.achieveCardMobile]}>
            <View style={styles.trophyBox}>
              <FeatherIcon name="award" size={20} color="#D97706" />
            </View>
            <View style={styles.achieveInfo}>
              <Text style={styles.achieveTitle}>{ach.title}</Text>
              <Text style={styles.achieveDate}>{formatMonthYear(ach.dateAwarded)}</Text>
            </View>
          </View>
        ))
      )}

      <Modal visible={showModal} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowModal(false)}>
              <FeatherIcon name="x" size={28} color="#FFF" />
            </TouchableOpacity>
          </View>
          {selectedImage && (
            <View style={styles.modalContent}>
              <GalleryImageView galleryId={selectedImage.id} style={[styles.fullImage, isMobile && styles.fullImageMobile]} containerStyle={[styles.fullImage, isMobile && styles.fullImageMobile]} />
            </View>
          )}
        </View>
      </Modal>
    </View>
  );

  if (isNested) return content;
  return <WebPageShell title="Gallery" maxWidth={1000}>{content}</WebPageShell>;
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  title: { fontSize: 22, fontWeight: '800', color: colors.textPrimary, marginBottom: 16 },
  loader: { marginTop: 40 },
  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60 },
  emptyText: { fontSize: 16, color: colors.textMuted, marginTop: 12, fontWeight: '600' },
  gridContainer: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, width: '100%', paddingBottom: 24 } as any,
  gridContainerMobile: { gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 } as any,
  imageContainer: { aspectRatio: 1, borderRadius: 12, overflow: 'hidden' },
  image: { width: '100%', height: '100%' },
  sectionTitle: { fontSize: 16, fontWeight: '800', color: colors.textPrimary, marginTop: 8, marginBottom: 16 },
  achieveCard: { flexDirection: 'row', backgroundColor: colors.card, borderRadius: 16, padding: 16, marginBottom: 12, alignItems: 'center', borderWidth: 1, borderColor: colors.divider },
  achieveCardMobile: { padding: 12 },
  trophyBox: { width: 44, height: 44, borderRadius: 22, backgroundColor: '#FEF3C7', justifyContent: 'center', alignItems: 'center', marginRight: 16 },
  achieveInfo: { flex: 1 },
  achieveTitle: { fontSize: 15, fontWeight: '800', color: colors.textPrimary, marginBottom: 4 },
  achieveDate: { fontSize: 13, fontWeight: '600', color: colors.textMuted },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.95)', justifyContent: 'center', alignItems: 'center' },
  modalHeader: { position: 'absolute', top: 16, right: 16, zIndex: 10 },
  modalContent: { alignItems: 'center', width: '100%' },
  fullImage: { width: '90%', height: '70%', borderRadius: 8, resizeMode: 'contain' },
  fullImageMobile: { width: '96%', height: '60%' },
});
